import { Howl, Howler } from 'howler';
import LocaleService from 'services/loader-handler-services/locale-service';
import AudioServiceBase from 'services/course-handler-services/audio-service-base';
import IconAtomView from 'views/atoms/icon-atom/icon-atom-view';
import { webStorage } from 'services/data-handler-services/web-storage-service';
import Chaplin from 'chaplin';
import utils from 'lib/utils';
import $ from 'jquery';
import UserAgentService from 'services/browser-handler-services/user-agent-service';
import EnrollmentDataService from 'services/data-handler-services/enrollment-data-service';
import ControlBarButtonAtomView from 'views/atoms/control-bar-button-atom/control-bar-button-atom-view';
import ControlBarMenuAtomView from 'views/atoms/control-bar-menu-atom/control-bar-menu-atom-view';

Howler.autoUnlock = true; // Howler will automatically attempt to enable audio.
const PLAYRATE_UPPER_LIMIT = 2;
const PLAYRATE_LOWER_LIMIT = 0.5;
export default class AudioServiceHowler extends AudioServiceBase {
  constructor(view) {
    super(view);
    this.index = 0; // Index of sounds in the playlist. initially 0 to start playing on the first sound in the list
    this.blockCID = view.model.get('cid');
    this.lastVolumeLevel = NaN;
    this.playBackRate = EnrollmentDataService.getByDotString('settings.audio.playbackRate') ?? 1; // in case default config is not set, fallback value is 1.
    this.audioMute = webStorage.getItem('audio_mute') || this.getEnrollmentDataMute(); // Audio mute setting can be set from a localStorage key or enrollment data setting
    this.soundPlaying = false;
    this.defaultVolumeLevel = 0.25; // Default volume level. Will be used when unmuting on '0' volume
    Howler.volume(this.defaultVolumeLevel); //Setting default volume level globally
  }

  /**
   * Gets the audio from the playlist by index.
   * When the index is empty it gets the first or current audio.
   * However rail guards are in place to throw an error when trying trying to get an audio that is not in the playlist.
   * @param {Number} index Audio index in the playlist
   */
  getAudioByIndex(index = undefined) {
    try {
      if (isNaN(index)) {
        index = this.index;
      }

      if (index < 0 || index > this.playlist.length) {
        throw Error(`Incorrect index: ${index} is not in the playlist`);
      }

      return this.playlist.find(audio => audio.index === index);
    } catch (e) {
      throw Error(`Could not get audio by index ${index}: ${e}`);
    }
  }

  /**
   * Plays audio from playlist.
   * When the index is undefined it gets the first or current audio.
   * @param {Number} index Audio index in the playlist
   */
  play(index) {
    // set current audio if index is not defined
    if (isNaN(index)) {
      index = this.index;
    }

    this.stopCurrent(); // avoids multiple audios playing at once

    const listItem = this.getAudioByIndex(index);
    const sound = listItem.howl;
    this.duration = sound.duration(); // update player duration display
    sound.rate(this.playBackRate);
    sound.play(); // begin playing the audio
    // Update player play/pause button
    if (sound.state() === 'loaded' || sound.state() === 'loading') {
      this.audioTimer(sound); // update player audio timer and progress bar
      this.playBtn.style.display = 'none';
      this.pauseBtn.style.display = 'block';
      this.playStateBtn.textContent = LocaleService.t('audio.playStatus');
      this.publishAudioEvent('playing', listItem);
      this.soundPlaying = true;
    } else {
      this.playStateBtn.textContent = LocaleService.t('audio.stopStatus');
      this.playBtn.style.display = 'block';
      this.pauseBtn.style.display = 'none';
      this.audioTimer(sound);
      this.publishAudioEvent('stopped', listItem);
      this.soundPlaying = false;
    }

    this.volume(this.audioMute ? 0 : Howler.volume()); // update player volume range slider and/or mute/unmute button
    this.index = index; // keep track of the current audio playing
  }

  /**
   * Pauses audio
   * When the index is undefined it gets the first or current audio.
   * @param {Number} index Audio index in the playlist
   */
  pause(index = undefined) {
    const sound = this.getAudioByIndex(index);
    if (sound.howl.playing()) {
      sound.howl.pause();
      this.playStateBtn.textContent = LocaleService.t('audio.pauseStatus');
      this.playBtn.style.display = 'block';
      this.pauseBtn.style.display = 'none';
      this.publishAudioEvent('paused', sound);
      this.soundPlaying = false;
    }
  }

  /**
   * Stops specified or current audio
   * @param {Number} index Audio index in the playlist
   */
  stop(index = undefined) {
    const sound = this.getAudioByIndex(index);
    if (sound.howl?.playing() || this.soundPlaying) {
      sound.howl?.stop();
      this.playStateBtn.textContent = LocaleService.t('audio.stopStatus');
      this.publishAudioEvent('stopped', sound);
      this.soundPlaying = false;
      this.hidePlayer();
    }
  }

  stopCurrent() {
    const sound = this.playlist[this.index].howl;

    if (sound?.playing()) {
      sound.stop();
    }
  }

  /**
   * Sets audio volume and updated volume bar.
   * When the index is not defined it sets global audio volume.
   * @param {Number} val Volume level 0 to 1
   * @param {Number} index Audio index in the playlist
   */
  volume(val = Howler.volume(), index = undefined) {
    let level = val;

    if (level > 1) {
      level = parseInt(val) / 100;
    }
    this.volumeBar.value = level * 100;
    this.lastVolumeLevel = this.volumeBar.value;
    this.mute(parseFloat(level) === 0);
    if (index) {
      const sound = this.getAudioByIndex(index);
      sound.howl.volume(level);
    } else {
      Howler.volume(level);
    }
  }

  /**
   * Mutes audio.
   * @param {Boolean} muteState
   */
  mute(muteState) {
    let volumeLevel = muteState ? 0 : this.lastVolumeLevel;
    if (!muteState && parseInt(this.lastVolumeLevel) === 0) {
      this.lastVolumeLevel = this.defaultVolumeLevel;
      volumeLevel = this.lastVolumeLevel * 100;
    }
    Howler.mute(muteState);
    Howler.volume(this.lastVolumeLevel);
    this.audioMute = muteState;

    // Update volume bar and mute buttons
    if (muteState) {
      this.volumeBar.value = volumeLevel;
      this.mutedBtn.style.display = 'block';
      this.unmutedBtn.style.display = 'none';
    } else {
      this.volumeBar.value = volumeLevel;
      this.mutedBtn.style.display = 'none';
      this.unmutedBtn.style.display = 'block';
    }
  }

  /**
   * Skips to the next or previous audio
   * @param {String} direction Direction to skip to (prev and next)
   */
  skip(direction, index = undefined) {
    if (index) {
      this.skipTo(index);
    } else {
      if (direction === 'prev') {
        index = this.index - 1;
        if (index < 0) {
          index = this.playlist.length - 1;
        }
      } else {
        index = this.index + 1;
        if (index >= this.playlist.length) {
          index = 0;
        }
      }
      this.skipTo(index);
    }
  }

  skipTo(index) {
    this.stopCurrent();
    this.play(index);
  }

  /**
   * Seek to a new position in the currently playing track.
   * At the moment  we will seek fixed steps
   * TODO: In future we might want to use the progress bar to seek
   * @param {String} direction Direction to seek in
   */
  seek(direction) {
    // Get the Howl we want to manipulate.
    const sound = this.playlist[this.index].howl;
    let seekPosition;
    const seekStep = 0.15;
    const currentPos = sound.seek() / sound.duration();

    if (currentPos === 0) {
      return;
    }

    if (direction === 'backward') {
      seekPosition = currentPos - seekStep;
    } else if (direction === 'forward') {
      seekPosition = currentPos + seekStep;
    }
    if (sound.playing()) {
      sound.seek(sound.duration() * Math.max(seekPosition, 0));
      if (UserAgentService.isIOS()) {
        this.audioTimer(sound);
      }
    }
  }

  /**
   * Sets the duration display
   */
  set duration(audioDuration) {
    const time = super.formatTime(Math.round(audioDuration ?? 0));
    this.durationElement.textContent = LocaleService.t(time);
  }

  /**
   * Attaches additional audio events from the audio view to audios in the playlist
   * available media events: https://developer.mozilla.org/en-US/docs/Web/Guide/Events/Media_events
   *
   * @param {Object} events media events
   */
  attachMediaEvents(events) {
    Howler.html5PoolSize = 100;
    Howler.autoUnlock = true;
    Howler.usingWebAudio = true;

    //const autoplay = !!this.audioView.model.get('autoplay');

    // sort playlist by index
    this.playlist.sort((a, b) => a.index - b.index);

    // audios in the playlist initially have null hows, thus we want to attach howls and events
    this.playlist.forEach(sound => {
      if (!sound.howl) {
        sound.howl = new Howl({
          src: sound.src,
          html5: true,
          format: 'mp3',
          preload: true,
          onplay: index => {
            const id = this.audioView.model.get('id');
            const a11yStatus = LocaleService.t('audio.playStatus');
            if (this.audioView.model.has('transcript')) {
              const transcript = this.audioView.model.get('transcript');
              this.audioView.publishEvent('media:narration', LocaleService.t(transcript.text));
            }
            this.audioView.publishEvent('media:status:changed', 'playing', id, 'audio', a11yStatus);
            this.playBtn.style.display = 'none';
            this.pauseBtn.style.display = 'block';
            const duration = sound.howl.duration(id);
            if (duration) {
              this.audioTimer(sound.howl);
            }
            if (sound.howl.volume() === 0 || this.audioMute) {
              this.mute(true);
            }
          },
          onload: () => {
            if (sound.howl.state() === 'loaded') {
              // block autoplay trumps source level autoplay
              if (!this.blockAutoplay) {
                return;
              }

              if (
                sound.autoplay &&
                sound.playOrder === 0 &&
                EnrollmentDataService.getByDotString('settings.audio.autoplay')
              ) {
                this.play(sound.index);
                this.showPlayer();
              }
            }
          },
          onloaderror: () => {
            // False positive for auto play audio - check sound howl first
            if (sound.howl) {
              Howler.autoUnlock = true;

              if (sound.autoplay && !this.soundPlaying) {
                this.soundPlaying = true;
                this.play();
              }
            } else {
              sound.howl.once('loaderror', () => {
                sound.howl.unload();
              });
            }
          },
          onplayerror: () => {
            if (this.playerError) {
              return;
            }
            this.playerError = true;
            this.statusBar.style.display = 'flex';
            // TODO: howler will start playing on any user interaction on a page, because it attempts to unblock itself.
            // If a more defined user interaction is required, uncomment the following line
            //this.audioView.publishEvent('audio:play:blocked');

            sound.howl.once('unlock', () => {
              this.statusBar.style.display = 'none';
              Howler.autoUnlock = true;
              // Prevent playing autoplay audio if a user triggers an interaction
              if (!this.playTriggeredAudio) {
                this.play(sound.index);
              }
              this.playerError = false;
            });
          },
          onend: () => {
            /**
             * for audio with autoplay and if the audio block is set to autoplay, we should play the next sound.
             * However audios triggered by events will not play next audio.
             */
            let canPlay = false;
            let nextSound = '';

            if (sound.playOrder !== false) {
              let orderedPlay = [];

              orderedPlay = this.autoplayableAudio;
              const [lastSound] = orderedPlay.slice(-1);

              if (lastSound.index !== sound.index) {
                nextSound = orderedPlay[sound.playOrder + 1];
                canPlay = nextSound && nextSound.playOrder !== false && nextSound.autoplay;
              }
            }

            if (canPlay) {
              this.skipTo(nextSound.index);
            } else {
              this.playBtn.style.display = 'block';
              this.pauseBtn.style.display = 'none';
              this.playStateBtn.textContent = LocaleService.t('audio.stopStatus');
              this.soundPlaying = false;
              this.publishAudioEvent('stopped', sound);
            }
          },
          onpaused: () => {},
          onseek: () => {},
          ...events,
        });
      }
    });
  }

  /**
   * Set up available player controls  and attach events on them
   * This should be fired when the block has rendered so that we have the controls available for manipulation in the DOM.
   */
  setupControls() {
    this.audioElement = this.audioView.el.querySelector(`.audio__player`);
    this.audioView.model.set('audioElement', this.audioElement);
    this.playBtn = this.audioElement.querySelector(`.audio__btn--play`);
    this.pauseBtn = this.audioElement.querySelector(`.audio__btn--pause`);
    this.nextBtn = this.audioElement.querySelector(`.audio__btn--next`);
    this.prevBtn = this.audioElement.querySelector(`.audio__btn--prev`);
    this.timeElement = this.audioElement.querySelector(`.audio__playback-time`);
    this.durationElement = this.audioElement.querySelector(`.audio__duration`);
    this.progressElem = this.audioElement.querySelector(`.audio__progress`);
    this.progressPlayElem = this.audioElement.querySelector(`.audio__progress--play`);
    this.volumeBar = this.audioElement.querySelector('.audio__btn--volume');

    // iOS and Android don't support JS-driven volume changes
    if (UserAgentService.isIOS() || UserAgentService.isMobile()) {
      this.volumeBar.style.display = 'none';
    }
    this.mutedBtn = this.audioElement.querySelector('.audio__btn--muted');
    this.unmutedBtn = this.audioElement.querySelector('.audio__btn--unmuted');
    // TODO: Unhide playlist button once we have a go ahead
    this.playlistBtn = this.audioElement.querySelector(`.audio__btn--playlist`);
    this.collapseBtn = this.audioElement.querySelector(`.audio__btn--collapse`);
    this.uncollapseBtn = this.audioElement.querySelector(`.audio__btn--uncollapse`);
    this.collpseText = this.audioElement.querySelector(`.audio__collapse-text`);
    this.collpseControl = this.audioElement.querySelector(`.audio__collapse-controls`);
    this.expandBtn = this.audioElement.querySelector(`.audio__btn--expand`);
    this.playerResize = this.audioElement.querySelector(`.audio__player--resize`);
    this.seekForwardBtn = this.audioElement.querySelector(`.audio__btn--seek-forward`);
    this.seekBackwardBtn = this.audioElement.querySelector(`.audio__btn--seek-backward`);
    this.playStateBtn = this.audioElement.querySelector(`.audio__state`);
    this.statusBar = this.audioElement.querySelector(`.audio__status-bar`);
    this.statusBar.style.display = 'none';
    this.createPlaylist();

    const btnSelectors = this.getIconSelectors();
    this.registerIcons(btnSelectors);
    this.setUpPlayRate();

    const mobileScreens = UserAgentService.matchMobileScreens();
    mobileScreens?.addEventListener('change', this.mediaQueryChange);

    this.playBtn.addEventListener('click', () => {
      this.play();
    });

    this.pauseBtn.addEventListener('click', () => {
      this.pause();
    });

    this.nextBtn.addEventListener('click', () => {
      this.skip('next');
    });

    this.prevBtn.addEventListener('click', () => {
      this.skip('prev');
    });

    this.volumeBar.addEventListener('click', event => {
      const val = event.currentTarget.value;
      if (val) {
        this.lastVolumeLevel = val;
        this.volume(val);
      }
    });

    this.mutedBtn.addEventListener('click', () => {
      this.mute(false);
    });

    this.unmutedBtn.addEventListener('click', () => {
      this.mute(true);
    });

    this.collpseControl.addEventListener('click', () => {
      this.audioElement.classList.toggle('collapsed');

      this.collpseText.textContent = LocaleService.t(
        this.audioElement.classList.contains('collapsed') ? 'audio.audio' : 'audio.hide'
      );
    });

    this.playerResize.addEventListener('click', () => {
      this.audioElement.classList.toggle('expanded');
      this.mediaQueryChange(mobileScreens);
    });

    this.seekForwardBtn.addEventListener('click', () => {
      this.seek('forward');
    });

    this.seekBackwardBtn.addEventListener('click', () => {
      this.seek('backward');
    });

    this.playBtn.style.display = 'block';
    this.pauseBtn.style.display = 'none';

    // playlist with not more than one audio file do not need next or prev controls
    if (this.playlist.length === 1) {
      this.nextBtn.style.display = 'none';
      this.prevBtn.style.display = 'none';
    }

    //by default the player should be uncollapsed
    this.collpseText.textContent = LocaleService.t('audio.hideStatus');
    this.playStateBtn.textContent = LocaleService.t('audio.stopStatus');

    this.playerOffset = this.audioElement;
  }
  renderControlBarAtoms() {
    const speedMenu = new ControlBarMenuAtomView({
      region: `speedMenuAtom-${this.blockCID}`,
      model: new Chaplin.Model({
        id: `speed-menu-${this.blockCID}`,
        cid: this.blockCID,
        orderedList: true,
        key: 'speed',
        title: LocaleService.t('video.speed'),
        hideInactiveBeforeIcons: true,
        backButtonTarget: 'settings',
        items: this.getSpeeds(),
      }),
    });
    const settingsBtnAtom = new ControlBarButtonAtomView({
      region: `settingsBtnAtom-${this.blockCID}`,
      model: new Chaplin.Model({
        id: 'settings-button',
        iconBefore: 'fas fa-cog',
        cid: this.blockCID,
        ariaLabel: LocaleService.t('video.settings'),
        actionTarget: 'video-settings-btn',
        classes: 'btn-compact',
        tippyConfig: {
          content: speedMenu.el,
          hideOnClick: 'toggle',
          animation: 'scale',
          trigger: 'mouseenter click',
          placement: 'bottom',
          onShow: instance => {
            const $popper = $(instance.popper);
            requestAnimationFrame(() => {
              $popper.find('.control-bar-menu-speed').show();
            });
          },
        },
      }),
    });
    this.audioView.subview(`speedMenuAtom-${this.blockCID}`, speedMenu);
    this.audioView.subview(`settingsBtnAtom-${this.blockCID}`, settingsBtnAtom);
    this.audioView.publishEvent(`add:tooltip:${this.blockCID}`);
  }
  setUpPlayRate() {
    this.audioView.registerRegion(`settingsBtnAtom-${this.blockCID}`, `.audio__btn--playRate`);
    this.audioView.registerRegion(`speedMenuAtom-${this.blockCID}`, `.audio__btn--playRateMenu`);
    this.renderControlBarAtoms();
  }

  setPlaybackRate(rate) {
    rate = Math.max(0.5, rate);
    const sound = this.getAudioByIndex();
    sound?.howl.rate(rate);
    EnrollmentDataService.saveDataByDotString('settings.audio.playbackRate', rate);
    this.playBackRate = rate;
    Chaplin.mediator.publish(`audio:playRate`, this.playBackRate);
  }

  getSpeeds() {
    const items = [];
    for (let rate = PLAYRATE_LOWER_LIMIT; rate <= PLAYRATE_UPPER_LIMIT; rate += PLAYRATE_LOWER_LIMIT) {
      items.push({
        title: LocaleService.t(`${rate}x`),
        onClick: this.setPlaybackRate.bind(this, rate),
        value: rate,
        key: rate,
        active: this.playBackRate === rate,
        before_icon: {
          icon: 'far fa-check',
          classes: 'fa-fw',
        },
      });
    }
    return items;
  }
  publishAudioEvent(state, sound = {}) {
    const eventData = {
      index: sound.index,
      id: sound.id,
      eventTarget: sound.eventTarget,
      duration: LocaleService.t(super.formatTime(Math.round(sound.howl?.duration() ?? 0))),
    };
    Chaplin.mediator.publish(`audio:${state}`, eventData);
  }

  /**
   * Stop, unload and destroy howler object
   */
  dispose() {
    this.soundPlaying = false;
    this.playlist.forEach(sound => {
      if (sound.howl) {
        sound.howl.stop(); // Stop playback
      }
    });
    this.playlist = [];
  }

  /**
   * Updates audio timer display based on audio playback progression.
   * @param {Object} audio Audio howl object
   */
  audioTimer(audio) {
    requestAnimationFrame(() => {
      const seek = audio?.seek() || 0;
      const duration = audio?.duration() || 0;
      const progress = (seek / duration) * 100;
      const percentageProgress = isNaN(progress) ? 0 : progress;
      this.timeElement.textContent = this.formatTime(Math.round(seek));
      this.progressElem.setAttribute('aria-valuenow', Math.ceil(percentageProgress));
      this.progressPlayElem.style.width = `${percentageProgress}%`;
      if (audio.playing() || this.soundPlaying) {
        this.audioTimer(audio);
      }
    });
  }

  /**
   * Creates a playlist UI and attached play events based on audio files in this.playlist
   */
  createPlaylist() {
    const listContainer = this.audioElement.querySelector(`.audio__playlist`);

    this.playlist.forEach((audio, index) => {
      const listItem = document.createElement('li');
      listItem.classList.add('audio__track');
      listItem.textContent = audio.id;

      listItem.addEventListener('click', () => {
        this.skipTo(index);
        listContainer.classList.remove('active');
      });

      listContainer.appendChild(listItem);
    });

    this.playlistBtn.addEventListener('click', () => {
      listContainer.classList.toggle('active');
    });
  }

  /**
   * Register player control button regions
   * @param {Object} buttons Array of button icon regions
   */
  registerIcons(buttons) {
    buttons.forEach(button => {
      const region = button.selector.substring(1);
      this.audioView.registerRegion(region, button.selector);

      const atom = this.getIconAtom(region, this.getIconModel(button.icon));
      this.audioView.subview(region, atom);
    });
  }

  /**
   * Gets a new icon model.
   * The default prefix is `fas`
   * @param {String} iconName Icon name
   */
  getIconModel(iconName) {
    return new Chaplin.Model({
      prefix: 'fas',
      icon: `fas ${iconName}`,
      classes: 'fa-fw',
    });
  }

  /**
   * Generates an icon
   * @param {String} region Region class to display the icon
   * @param {*} iconModel
   */
  getIconAtom(region, iconModel) {
    return new IconAtomView({
      region: region,
      model: iconModel,
    });
  }

  /**
   * Gets a static Array of icons required for the audio player controls
   * TODO: have a dynamic list based on the audio control hbs
   */
  getIconSelectors() {
    return [
      { selector: `.audio__btn--play`, icon: 'fa-play' },
      { selector: `.audio__btn--pause`, icon: 'fa-pause' },
      { selector: `.audio__btn--next`, icon: 'fa-step-forward' },
      { selector: `.audio__btn--prev`, icon: 'fa-step-backward' },
      { selector: `.audio__btn--playlist`, icon: 'fa-list' },
      { selector: '.audio__btn--muted', icon: 'fa-volume-mute' },
      { selector: '.audio__btn--unmuted', icon: 'fa-volume-up' },
      { selector: '.audio__btn--uncollapse', icon: 'fa-chevron-circle-left' },
      { selector: '.audio__btn--collapse', icon: 'fa-chevron-circle-right' },
      { selector: '.audio__btn--expand', icon: 'fa-expand-alt' },
      { selector: '.audio__btn--compress', icon: 'fa-compress-alt' },
      { selector: '.audio__btn--seek-forward', icon: 'fa-redo' },
      { selector: '.audio__btn--seek-backward', icon: 'fa-undo' },
    ];
  }

  /**
   * Gets the muted state from enrollment data.
   * This is just a normalizer.
   */
  getEnrollmentDataMute() {
    return this.audioView.model.get('muted');
  }
  /**
   * Set the player top offset based the platform header
   */
  set playerOffset(player) {
    const platformHeight = utils.getPlatformHeaderHeight();
    const computedStyles = window.getComputedStyle(player);
    const playerOffset = computedStyles.getPropertyValue('top');
    const topOffset = utils.pixelMath(platformHeight, '+', playerOffset);
    player.style.top = topOffset;
  }

  mediaQueryChange(e) {
    const container = $('#page-container');
    const audioWrapper = $('.audioWrapper');

    if (e.matches && audioWrapper.hasClass('playerActive')) {
      const audioPlayerHeight = $('.audio__player').outerHeight(true) + 10; // add 10 more pixels to give the player some space

      container.css({
        'padding-bottom': audioPlayerHeight,
      });
    } else {
      container.css({
        'padding-bottom': 'initial',
      });
    }
  }

  showPlayer() {
    if (!this.audioView.hasControls) {
      return;
    }
    const wrapper = this.audioView.$el.find('.audioWrapper');
    if (!wrapper.hasClass('playerActive')) {
      wrapper.addClass('playerActive');
      const mobileScreens = UserAgentService.matchMobileScreens();
      this.mediaQueryChange(mobileScreens);
    }
  }
  hidePlayer() {
    if (!this.audioView.hasControls) {
      return;
    }
    const wrapper = this.audioView.$el.find('.audioWrapper');
    wrapper.toggleClass('playerActive');
  }

  get autoplayableAudio() {
    return this.playlist.filter(audio => audio.playOrder !== false).sort((a, b) => a.playOrder - b.playOrder);
  }

  get blockAutoplay() {
    return !!this.audioView?.model?.get('autoplay');
  }
}
